import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { playDing } from "@/lib/audio";
import * as notify from "@/lib/notify";
import { Bell, BellRinging, CheckCircle, XCircle, UploadSimple, SignIn, Info, ArrowRight } from "@phosphor-icons/react";

const POLL_MS = 15000;

// Route notifications from the header context (admin vs customer) so that
// clicking the OS notification lands on the right page. Passed via prop below.
function urlForNotification(n, viewAllHref) {
    const meta = n.meta || {};
    if (n.type === "slip_upload") return "/admin/uploads?status=pending";
    if (n.type === "slip_verified" || n.type === "slip_rejected" || n.type === "slip_updated") {
        return meta.utr ? `/admin/uploads?utr=${encodeURIComponent(meta.utr)}` : viewAllHref;
    }
    if (n.type === "party_login" && meta.party_id) return `/admin/transactions?party_id=${meta.party_id}`;
    return viewAllHref;
}

const ICONS = {
    party_login: SignIn,
    slip_upload: UploadSimple,
    slip_verified: CheckCircle,
    slip_rejected: XCircle,
    slip_updated: Info,
};

const ACCENT = {
    party_login: "text-sky-600 bg-sky-50",
    slip_upload: "text-amber-600 bg-amber-50",
    slip_verified: "text-emerald-600 bg-emerald-50",
    slip_rejected: "text-rose-600 bg-rose-50",
    slip_updated: "text-violet-600 bg-violet-50",
};

const timeAgo = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

/**
 * Bell icon + dropdown + toast/chime for new items.
 * Polls /api/notifications every 15 s.
 * Prop: viewAllHref — where clicking "View all" should link (e.g. "/admin/notifications").
 */
export default function NotificationsBell({ viewAllHref = "/admin/notifications", compact = false }) {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [permState, setPermState] = useState(notify.permission());
    const lastSeenIdsRef = useRef(new Set());
    const initialisedRef = useRef(false);
    const panelRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/notifications", { params: { limit: 15 } });
            const list = Array.isArray(data) ? data : [];
            setItems(list);
            setUnread(list.filter((n) => !n.read).length);

            const currentIds = new Set(list.map((n) => n.id));
            if (initialisedRef.current) {
                // Detect brand-new items (not in the previous snapshot) — announce them.
                const fresh = list.filter((n) => !lastSeenIdsRef.current.has(n.id) && !n.read);
                if (fresh.length > 0) {
                    playDing();
                    const top = fresh[0];
                    // v1.28.94 — Deduped notification surface. When the tab is
                    // visible the sonner toast (top-right) is the ONE UI signal
                    // for the admin — no `notify.showOS()` bubbles here. The
                    // service-worker push handler (see /public/service-worker.js)
                    // is now gated by a focused-client check, so when the tab is
                    // NOT focused it fires the single OS bubble instead. This
                    // eliminates the "three notifications for one slip upload"
                    // bug (sonner + `notify.showOS` + SW push all firing at
                    // once when admin had the app open).
                    toast(top.title, { description: top.body, duration: 6000 });
                    // v1.25.53 — If any fresh notification is a slip
                    // verification OR a payout received meant for THIS
                    // customer, fire the fancy 3D shards overlay even if
                    // the OS push never arrived (works as long as the app
                    // is open). CustomerLayout listens for the
                    // `micm-thanks-fire` event.
                    // v1.25.70 — Payout notifications also fire the overlay
                    // (with payout-specific copy).
                    const thanks = fresh.find((n) => n.type === "slip_verified" || n.type === "payout_received");
                    if (thanks) {
                        try {
                            const kind = thanks.type === "payout_received" ? "payout" : "verified";
                            window.dispatchEvent(new CustomEvent("micm-thanks-fire", {
                                detail: { kind, title: thanks.title, body: thanks.body, meta: thanks.meta || {} },
                            }));
                        } catch { /* noop */ }
                    }
                }
            } else {
                initialisedRef.current = true;
            }
            lastSeenIdsRef.current = currentIds;
        } catch (_e) { /* silent polling */ }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close popover on outside click
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const markAllRead = async () => {
        try {
            await api.post("/notifications/mark-read", {});
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnread(0);
        } catch (_e) { /* ignore */ }
    };

    const markOneRead = async (id) => {
        try {
            await api.post("/notifications/mark-read", { ids: [id] });
            setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
            setUnread((c) => Math.max(0, c - 1));
        } catch (_e) { /* ignore */ }
    };

    const enableOSAlerts = async () => {
        const p = await notify.ensurePermission();
        setPermState(p);
        if (p === "granted") {
            // v1.28.94 — Welcome bubble stays because it confirms the OS
            // permission actually worked (this is user-gesture-driven, one
            // shot). Runtime notifications no longer fire showOS from here;
            // the service worker handles the offline/hidden case and the
            // in-app sonner toast handles the focused case.
            toast.success("System alerts enabled · you'll get a sound + popup for every new event");
            notify.showOS("Notifications enabled", {
                body: "You'll hear a sound whenever a party logs in, uploads a slip, or your slip is verified.",
                tag: "welcome",
                url: viewAllHref,
            });
        } else if (p === "denied") {
            toast.error("Blocked. Open site settings and allow notifications, then reload.");
        }
    };

    return (
        <div className="relative" ref={panelRef}>
            <button
                type="button"
                data-testid="notifications-bell"
                aria-label={`Notifications ${unread ? `(${unread} unread)` : ""}`}
                onClick={() => setOpen((v) => !v)}
                className={`relative flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 ${compact ? "h-7 w-7" : "h-9 w-9"}`}
            >
                <Bell size={compact ? 14 : 17} weight={unread ? "fill" : "regular"} className={unread ? "text-amber-500" : ""} />
                {unread > 0 && (
                    <span
                        data-testid="notifications-badge"
                        className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white ring-2 ring-white"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notifications-panel"
                    className="fixed inset-x-3 top-16 z-40 max-h-[80vh] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-sm"
                >
                    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2.5">
                        <div className="font-display text-sm font-semibold text-slate-900">Notifications</div>
                        <button
                            type="button"
                            data-testid="notifications-mark-all-read"
                            onClick={markAllRead}
                            disabled={unread === 0}
                            className="text-[11px] font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40"
                        >
                            Mark all read
                        </button>
                    </div>
                    {permState !== "granted" && notify.isSupported() && (
                        <div className="flex items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2">
                            <div className="flex items-center gap-2 text-[11px] text-amber-800">
                                <BellRinging size={13} weight="fill" />
                                <span>Turn on system alerts to get a sound + popup like WhatsApp.</span>
                            </div>
                            <button
                                data-testid="enable-os-alerts"
                                onClick={enableOSAlerts}
                                className="rounded-md bg-amber-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-amber-700"
                            >
                                {permState === "denied" ? "Blocked" : "Enable"}
                            </button>
                        </div>
                    )}
                    <div className="max-h-[65vh] overflow-y-auto">
                        {items.length === 0 && (
                            <div className="px-4 py-10 text-center text-xs text-slate-400">
                                You're all caught up.
                            </div>
                        )}
                        {items.map((n) => {
                            const Icon = ICONS[n.type] || Bell;
                            const accent = ACCENT[n.type] || "text-slate-500 bg-slate-100";
                            return (
                                <button
                                    key={n.id}
                                    type="button"
                                    data-testid={`notification-item-${n.id}`}
                                    onClick={() => markOneRead(n.id)}
                                    className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition hover:bg-slate-50 ${
                                        n.read ? "" : "bg-blue-50/40"
                                    }`}
                                >
                                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${accent}`}>
                                        <Icon size={15} weight="bold" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className={`truncate text-xs font-semibold ${n.read ? "text-slate-700" : "text-slate-900"}`}>
                                                {n.title}
                                            </div>
                                            {!n.read && (
                                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                                            )}
                                        </div>
                                        {n.body && <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">{n.body}</div>}
                                        <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">{timeAgo(n.created_at)}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    <Link
                        to={viewAllHref}
                        onClick={() => setOpen(false)}
                        data-testid="notifications-view-all"
                        className="flex items-center justify-center gap-1 border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs font-semibold text-blue-600 hover:bg-slate-100"
                    >
                        View all <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            )}
        </div>
    );
}
